import type { SchemaData } from '../schema/types'

/** URL slug of every line, made unique across the map. */
function keysByLineId(schema: SchemaData): Map<string, string> {
  const slugById = new Map<string, string>()
  for (const station of schema.stations) {
    if (!slugById.has(station.line.id)) {
      slugById.set(station.line.id, station.line.slug ?? station.line.id)
    }
  }
  const users = new Map<string, number>()
  for (const slug of slugById.values()) users.set(slug, (users.get(slug) ?? 0) + 1)
  const keys = new Map<string, string>()
  for (const [lineId, slug] of slugById) {
    keys.set(lineId, users.get(slug) === 1 ? slug : `${slug}-${lineId.toLowerCase()}`)
  }
  return keys
}

/**
 * The `<key>` of a line view URL `/<locale>/lines/<key>`. It is the line's
 * schema slug — the same slug the prerendered London line pages use — unless
 * several lines share it: New York names routes, not lines, so the 2 and the 3
 * are both `7-avenue-express`. Those get the line id appended
 * (`7-avenue-express-2`, `7-avenue-express-3`), so every key opens exactly one
 * line.
 */
export const LineUrlKey = Object.freeze({
  of(schema: SchemaData, lineId: string): string {
    return keysByLineId(schema).get(lineId) ?? lineId
  },
  /** The line a URL key names, or `null` when none does. */
  lineIdOf(schema: SchemaData, key: string): string | null {
    for (const [lineId, candidate] of keysByLineId(schema)) {
      if (candidate === key) return lineId
    }
    return null
  },
})
